/**
 * Prompts por categoria: indice (que version rige por categoria y clave) y detalle por scope/categoria/clave.
 * Mismo patron que `products`: se muestra lo ultimo conocido mientras se refresca.
 */
import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";

import { api } from "@/lib/api";
import type { Category, PromptDetailResponse, PromptKey, PromptScope, PromptsIndexResponse } from "@/lib/contracts";

export const detailKey = (scope: PromptScope, category: Category, key: PromptKey) => `${scope}:${category}:${key}`;

export const usePromptsStore = defineStore("prompts", () => {
  const index = shallowRef<PromptsIndexResponse | null>(null);
  const details = ref<Record<string, PromptDetailResponse>>({});

  async function refreshIndex(): Promise<PromptsIndexResponse> {
    index.value = await api.listPrompts();
    return index.value;
  }

  function store(detail: PromptDetailResponse): PromptDetailResponse {
    details.value = { ...details.value, [detailKey(detail.scope, detail.category, detail.key)]: detail };
    index.value = null; // el indice cambio; la pagina de indice lo vuelve a pedir
    return detail;
  }

  async function load(scope: PromptScope, category: Category, key: PromptKey): Promise<PromptDetailResponse> {
    const detail = await api.getPrompt(scope, category, key);
    details.value = { ...details.value, [detailKey(scope, category, key)]: detail };
    return detail;
  }

  async function save(
    scope: PromptScope,
    category: Category,
    key: PromptKey,
    text: string,
    baseVersion: number,
    note: string,
  ): Promise<PromptDetailResponse> {
    return store(await api.savePrompt(scope, category, key, text, baseVersion, note));
  }

  async function restore(
    scope: PromptScope,
    category: Category,
    key: PromptKey,
    version: number,
    baseVersion: number,
  ): Promise<PromptDetailResponse> {
    return store(await api.restorePrompt(scope, category, key, version, baseVersion));
  }

  /** Quita el override de la tienda (vuelve al global) y recarga el detalle. */
  async function removeOverride(category: Category, key: PromptKey): Promise<PromptDetailResponse> {
    await api.deleteTenantPrompt(category, key);
    index.value = null;
    return load("tenant", category, key);
  }

  function reset(): void {
    index.value = null;
    details.value = {};
  }

  return { index, details, refreshIndex, load, save, restore, removeOverride, reset };
});
